export interface DemoResponse {
  keywords: string[];
  response: string;
}

export interface ExampleImage {
  url: string;
  label: string;
}

export interface ImageStyle {
  id: string;
  label: string;
  icon: string;
}

export interface ImageRatio {
  id: string;
  label: string;
  icon: string;
}

export interface AiPlan {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted: boolean;
}

export const chatQuickPrompts: string[] = [
  'Rédige un email professionnel',
  'Idées de business rentables au Togo',
  'Explique-moi la programmation simplement',
  'Traduis en anglais : Bonjour, comment allez-vous ?',
  'Un plan marketing pour ma startup',
  'Écris un code de formulaire web',
];

export const demoResponses: DemoResponse[] = [
  {
    keywords: ['email', 'mail', 'message pro', 'courrier'],
    response:
      "Bien sûr ! Voici un exemple d'email professionnel :\n\nObjet : Proposition de collaboration\n\nBonjour [Nom],\n\nJ'espère que vous allez bien. Je me permets de vous contacter pour vous proposer une collaboration qui pourrait apporter une vraie valeur à votre activité.\n\nSeriez-vous disponible pour en échanger cette semaine ?\n\nCordialement,\n[Votre nom]",
  },
  {
    keywords: ['business', 'idée', 'rentable', 'entreprendre', 'opportunité'],
    response:
      "Voici quelques idées de business prometteuses en Afrique francophone :\n\n1. E-commerce local avec livraison (produits alimentaires, mode)\n2. Services numériques : création de sites, design, marketing\n3. Agritech : plateforme reliant agriculteurs et acheteurs\n4. Formation en ligne (compétences tech et métiers)\n5. Logistique et livraison du dernier kilomètre\n6. Énergie solaire et solutions d'accès à l'électricité\n\nJe peux approfondir n'importe laquelle avec un plan détaillé.",
  },
  {
    keywords: ['code', 'programmation', 'programmer', 'développeur', 'formulaire', 'javascript', 'python'],
    response:
      "Voici un exemple de formulaire web simple en HTML :\n\n<form>\n  <input type=\"text\" placeholder=\"Votre nom\" />\n  <input type=\"email\" placeholder=\"Votre email\" />\n  <button type=\"submit\">Envoyer</button>\n</form>\n\nSi vous voulez un exemple dans un langage précis (Python, JavaScript, PHP...), précisez-le et je vous donne un code complet et commenté.",
  },
  {
    keywords: ['traduis', 'traduction', 'anglais', 'english', 'translate'],
    response:
      "Voici la traduction :\n\n« Bonjour, comment allez-vous ? »\n→ « Hello, how are you? »\n\nJe peux traduire vos textes vers et depuis le français, l'anglais, l'espagnol, l'allemand et bien d'autres langues. Envoyez-moi le texte à traduire.",
  },
  {
    keywords: ['marketing', 'publicité', 'plan marketing', 'promouvoir', 'communication'],
    response:
      "Voici un plan marketing en 5 étapes :\n\n1. Définir votre cible (âge, localisation, besoins)\n2. Positionner votre offre (ce qui vous rend unique)\n3. Choisir vos canaux (réseaux sociaux, SEO, partenariats)\n4. Créer un contenu régulier et engageant\n5. Mesurer et ajuster (taux de conversion, retours clients)\n\nVoulez-vous que je détaille une étape en particulier ?",
  },
  {
    keywords: ['cv', 'curriculum', 'lettre de motivation', 'candidature'],
    response:
      "Pour un CV efficace, suivez cette structure :\n\n1. En-tête : nom, titre, contacts (téléphone, email, ville)\n2. Profil : 2-3 phrases qui résument votre valeur\n3. Expériences : poste, entreprise, dates, 2-3 réalisations chiffrées\n4. Formation : diplômes et certifications\n5. Compétences : techniques et transversales\n\nJe peux vous aider à rédiger chaque section, envoyez-moi vos infos.",
  },
  {
    keywords: ['finance', 'budget', 'argent', 'investir', 'épargne'],
    response:
      "Quelques principes de base pour gérer son budget :\n\n1. Suivez vos revenus et dépenses chaque mois\n2. Appliquez la règle 50/30/20 (besoins / envies / épargne)\n3. Constituez une épargne de sécurité (3 à 6 mois de dépenses)\n4. Investissez dans ce que vous comprenez\n5. Diversifiez pour limiter les risques\n\nSouhaitez-vous un exemple de budget mensuel concret ?",
  },
  {
    keywords: ['santé', 'médecin', 'symptôme', 'maladie'],
    response:
      "Je peux vous donner des informations générales sur la santé, mais je ne remplace pas un avis médical. En cas de symptôme ou d'inquiétude, consultez un professionnel de santé.\n\nPouvez-vous préciser votre question ? Je vous orienterai au mieux.",
  },
];

export const fallbackResponse =
  "Bonne question ! Voici ce que je peux vous dire : je suis votre assistant IA au sein de l'écosystème WorldApeiron. Je peux vous aider à rédiger, programmer, traduire, générer des idées, analyser des documents et bien plus.\n\nPrécisez votre demande (ex : « rédige un email », « idée de business », « code en Python ») et je vous répondrai de façon détaillée.";

export const imageStyles: ImageStyle[] = [
  { id: 'photorealistic', label: 'Photoréaliste', icon: 'ri-camera-line' },
  { id: 'illustration', label: 'Illustration', icon: 'ri-brush-line' },
  { id: 'minimal', label: 'Minimaliste', icon: 'ri-layout-line' },
  { id: '3d', label: '3D', icon: 'ri-box-3-line' },
];

export const imageRatios: ImageRatio[] = [
  { id: 'landscape', label: 'Paysage', icon: 'ri-landscape-line' },
  { id: 'portrait', label: 'Portrait', icon: 'ri-smartphone-line' },
  { id: 'squarish', label: 'Carré', icon: 'ri-aspect-ratio-line' },
];

export const exampleImages: ExampleImage[] = [
  {
    url: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20logo%20design%20for%20a%20tech%20startup%2C%20geometric%20golden%20shapes%2C%20clean%20professional%20branding%2C%20elegant%20typography%2C%20flat%20vector%20style%2C%20warm%20neutral%20background%2C%20high%20detail&width=800&height=600&seq=ai-example-01&orientation=landscape',
    label: 'Logo moderne',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=Professional%20business%20flyer%20design%20with%20vibrant%20warm%20colors%2C%20modern%20promotional%20layout%2C%20product%20showcase%2C%20clean%20typography%2C%20editorial%20graphic%20design%2C%20eye-catching%20composition&width=800&height=600&seq=ai-example-02&orientation=landscape',
    label: 'Flyer professionnel',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=Product%20mockup%20of%20a%20smartphone%20on%20a%20clean%20desk%2C%20soft%20natural%20lighting%2C%20minimal%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20product%20photography%2C%20shallow%20depth%20of%20field&width=800&height=600&seq=ai-example-03&orientation=landscape',
    label: 'Mockup produit',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20an%20African%20entrepreneur%20working%20on%20a%20laptop%2C%20warm%20amber%20and%20golden%20color%20palette%2C%20modern%20flat%20illustration%20style%2C%20clean%20composition%2C%20inspiring%20atmosphere&width=800&height=600&seq=ai-example-04&orientation=landscape',
    label: 'Illustration business',
  },
];

export const aiPlans: AiPlan[] = [
  {
    name: 'Gratuit',
    price: '0 FCFA',
    description: 'Pour découvrir l\'assistant IA au quotidien.',
    features: [
      '20 messages par jour',
      'Chat + traduction',
      'Rédaction assistée',
      'Modèle standard',
    ],
    highlighted: false,
  },
  {
    name: 'Standard',
    price: '5 000 FCFA/mois',
    description: 'Pour les indépendants et créateurs actifs.',
    features: [
      'Messages illimités',
      'Génération d\'images (50/mois)',
      'Analyse de documents',
      'Assistant code',
    ],
    highlighted: true,
  },
  {
    name: 'Premium',
    price: '15 000 FCFA/mois',
    description: 'Pour les professionnels exigeants.',
    features: [
      'Modèles avancés',
      'Images illimitées',
      'Fichiers volumineux',
      'Voix & audio',
      'Priorité de support',
    ],
    highlighted: false,
  },
  {
    name: 'Business',
    price: '50 000 FCFA/mois',
    description: 'Pour les équipes et entreprises.',
    features: [
      'Accès API',
      'Plusieurs membres',
      'Modèles dédiés',
      'SLA et support dédié',
      'Sécurité renforcée',
    ],
    highlighted: false,
  },
];